import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import {
  Card,
  Button,
  Input,
  Textarea,
  PageHeader,
  Spinner,
  EmptyState,
  Modal,
  Field,
  Select,
  Badge,
  Tabs,
  ErrorBanner,
  formatDate,
} from '../components/ui';

interface Page {
  id: string;
  slug: string;
  title: string;
  status: 'draft' | 'published';
  metaDescription?: string;
  blocks?: Block[];
  publishedAt?: string;
  createdAt: string;
}

interface Block {
  id: string;
  type: 'heading' | 'paragraph' | 'image' | 'button' | 'columns' | 'form';
  content: Record<string, unknown>;
}

const blockLibrary: { type: Block['type']; label: string }[] = [
  { type: 'heading', label: 'Heading' },
  { type: 'paragraph', label: 'Paragraph' },
  { type: 'image', label: 'Image' },
  { type: 'button', label: 'Button' },
  { type: 'columns', label: 'Columns' },
  { type: 'form', label: 'Form' },
];

export function PagesPage() {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['pages'],
    queryFn: () => api.get<{ data: Page[] }>('/pages').catch(() => ({ data: [] })),
  });

  const pages = q.data?.data ?? [];

  return (
    <div>
      <PageHeader
        title="Pages"
        subtitle="Landing pages, funnels, and standalone pages"
        action={<Button onClick={() => setShowCreate(true)}>+ New page</Button>}
      />

      {q.isLoading ? (
        <Spinner />
      ) : pages.length === 0 ? (
        <Card>
          <EmptyState
            title="No pages yet"
            description="Build your first landing page or funnel."
            action={<Button onClick={() => setShowCreate(true)}>+ New page</Button>}
          />
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {pages.map((p) => (
            <Card key={p.id} onClick={() => setEditingId(p.id)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{p.title}</div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                    /{p.slug} · {formatDate(p.createdAt)}
                  </div>
                </div>
                <Badge color={p.status === 'published' ? 'green' : 'gray'}>{p.status}</Badge>
              </div>
            </Card>
          ))}
        </div>
      )}

      {showCreate && (
        <CreatePageModal
          onClose={() => setShowCreate(false)}
          onSaved={(id) => {
            qc.invalidateQueries({ queryKey: ['pages'] });
            setShowCreate(false);
            setEditingId(id);
          }}
        />
      )}

      {editingId && (
        <PageEditor
          pageId={editingId}
          onClose={() => setEditingId(null)}
          onSaved={() => qc.invalidateQueries({ queryKey: ['pages'] })}
        />
      )}
    </div>
  );
}

function CreatePageModal({ onClose, onSaved }: { onClose: () => void; onSaved: (id: string) => void }) {
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [template, setTemplate] = useState<'blank' | 'landing' | 'thank_you'>('blank');
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>('/pages', {
        title,
        slug: slug || title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
        template,
        status: 'draft',
      }),
    onSuccess: (p) => onSaved(p.id),
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Failed'),
  });

  return (
    <Modal onClose={onClose} title="New page">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!title) return setError('Title required');
          mut.mutate();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      >
        <Field label="Title *">
          <Input value={title} onChange={setTitle} autoFocus />
        </Field>
        <Field label="URL slug" hint="Leave blank to auto-generate">
          <Input value={slug} onChange={setSlug} placeholder="welcome" />
        </Field>
        <Field label="Template">
          <Select
            value={template}
            onChange={setTemplate}
            options={[
              { value: 'blank', label: 'Blank' },
              { value: 'landing', label: 'Landing page' },
              { value: 'thank_you', label: 'Thank you page' },
            ]}
          />
        </Field>
        <ErrorBanner error={error} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={mut.isPending}>
            {mut.isPending ? 'Creating…' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function PageEditor({
  pageId,
  onClose,
  onSaved,
}: {
  pageId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'content' | 'seo' | 'analytics' | 'ab'>('content');
  const [error, setError] = useState<string | null>(null);

  const pageQ = useQuery({
    queryKey: ['pages', pageId],
    queryFn: () => api.get<Page>(`/pages/${pageId}`),
  });

  const blocksQ = useQuery({
    queryKey: ['page-blocks', pageId],
    queryFn: () => api.get<{ data: Block[] }>(`/pages/${pageId}/blocks`).catch(() => ({ data: [] })),
  });

  const [blocks, setBlocks] = useState<Block[] | null>(null);
  const currentBlocks = blocks ?? blocksQ.data?.data ?? [];

  const saveBlocksMut = useMutation({
    mutationFn: () => api.put(`/pages/${pageId}/blocks`, { blocks: currentBlocks }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['page-blocks', pageId] });
      onSaved();
    },
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Save failed'),
  });

  const publishMut = useMutation({
    mutationFn: () => api.post(`/pages/${pageId}/publish`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pages', pageId] });
      onSaved();
    },
  });

  const unpublishMut = useMutation({
    mutationFn: () => api.post(`/pages/${pageId}/unpublish`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pages', pageId] });
      onSaved();
    },
  });

  function addBlock(type: Block['type']) {
    const id = `b_${Math.random().toString(36).slice(2, 10)}`;
    const defaults: Record<Block['type'], Record<string, unknown>> = {
      heading: { text: 'Your headline', level: 1 },
      paragraph: { text: 'Tell your story…' },
      image: { src: '', alt: '' },
      button: { label: 'Click me', href: '/', variant: 'primary' },
      columns: { count: 2, items: ['', ''] },
      form: { formId: '' },
    };
    setBlocks([...(currentBlocks ?? []), { id, type, content: defaults[type] }]);
  }

  function updateBlock(id: string, content: Record<string, unknown>) {
    setBlocks((currentBlocks ?? []).map((b) => (b.id === id ? { ...b, content: { ...b.content, ...content } } : b)));
  }

  function moveBlock(id: string, dir: -1 | 1) {
    const next = (currentBlocks ?? []).slice();
    const idx = next.findIndex((b) => b.id === id);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setBlocks(next);
  }

  function removeBlock(id: string) {
    setBlocks((currentBlocks ?? []).filter((b) => b.id !== id));
  }

  if (pageQ.isLoading) {
    return (
      <Modal onClose={onClose} title="Loading…">
        <Spinner />
      </Modal>
    );
  }

  const page = pageQ.data!;

  return (
    <Modal onClose={onClose} title={page.title} width={840}>
      <Tabs<'content' | 'seo' | 'analytics' | 'ab'>
        active={tab}
        onChange={setTab}
        tabs={[
          { value: 'content', label: 'Content' },
          { value: 'seo', label: 'SEO' },
          { value: 'analytics', label: 'Analytics' },
          { value: 'ab', label: 'A/B test' },
        ]}
      />

      {tab === 'content' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {currentBlocks.length === 0 && (
            <p style={{ color: 'var(--color-text-tertiary)', fontSize: 'var(--text-sm)' }}>
              Empty page. Add blocks below.
            </p>
          )}
          {currentBlocks.map((b, idx) => (
            <Card key={b.id} style={{ padding: 'var(--space-3)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-2)' }}>
                <strong style={{ fontSize: 'var(--text-sm)' }}>
                  {idx + 1}. {b.type}
                </strong>
                <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
                  <Button size="sm" variant="ghost" onClick={() => moveBlock(b.id, -1)}>
                    ↑
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => moveBlock(b.id, 1)}>
                    ↓
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => removeBlock(b.id)}>
                    ×
                  </Button>
                </div>
              </div>
              <BlockEditor block={b} onChange={(c) => updateBlock(b.id, c)} />
            </Card>
          ))}
          <div style={{ marginTop: 'var(--space-2)', display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {blockLibrary.map((b) => (
              <Button key={b.type} size="sm" variant="secondary" onClick={() => addBlock(b.type)}>
                + {b.label}
              </Button>
            ))}
          </div>
          <ErrorBanner error={error} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 'var(--space-3)' }}>
            {page.status === 'published' ? (
              <Button variant="secondary" onClick={() => unpublishMut.mutate()}>
                Unpublish
              </Button>
            ) : (
              <Button onClick={() => publishMut.mutate()} disabled={publishMut.isPending}>
                {publishMut.isPending ? 'Publishing…' : 'Publish'}
              </Button>
            )}
            <Button onClick={() => saveBlocksMut.mutate()} disabled={saveBlocksMut.isPending}>
              {saveBlocksMut.isPending ? 'Saving…' : 'Save content'}
            </Button>
          </div>
        </div>
      )}

      {tab === 'seo' && <SeoTab pageId={pageId} page={page} onSaved={onSaved} />}
      {tab === 'analytics' && <AnalyticsTab pageId={pageId} />}
      {tab === 'ab' && <AbTestTab pageId={pageId} />}
    </Modal>
  );
}

function BlockEditor({ block, onChange }: { block: Block; onChange: (c: Record<string, unknown>) => void }) {
  switch (block.type) {
    case 'heading':
      return (
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <Input
            value={String(block.content.text ?? '')}
            onChange={(v) => onChange({ text: v })}
            style={{ flex: 1 }}
          />
          <Select
            value={String(block.content.level ?? '1')}
            onChange={(v) => onChange({ level: Number(v) })}
            options={[
              { value: '1', label: 'H1' },
              { value: '2', label: 'H2' },
              { value: '3', label: 'H3' },
            ]}
          />
        </div>
      );
    case 'paragraph':
      return <Textarea value={String(block.content.text ?? '')} onChange={(v) => onChange({ text: v })} rows={3} />;
    case 'image':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <Input value={String(block.content.src ?? '')} onChange={(v) => onChange({ src: v })} placeholder="Image URL" />
          <Input value={String(block.content.alt ?? '')} onChange={(v) => onChange({ alt: v })} placeholder="Alt text" />
        </div>
      );
    case 'button':
      return (
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <Input value={String(block.content.label ?? '')} onChange={(v) => onChange({ label: v })} placeholder="Label" />
          <Input value={String(block.content.href ?? '')} onChange={(v) => onChange({ href: v })} placeholder="Link" />
        </div>
      );
    case 'columns':
      return (
        <Field label="Number of columns">
          <Input value={Number(block.content.count ?? 2)} onChange={(v) => onChange({ count: Number(v) })} type="number" min={1} max={6} />
        </Field>
      );
    case 'form':
      return <Input value={String(block.content.formId ?? '')} onChange={(v) => onChange({ formId: v })} placeholder="Form ID" />;
  }
}

function SeoTab({ pageId, page, onSaved }: { pageId: string; page: Page; onSaved: () => void }) {
  const [title, setTitle] = useState(page.title);
  const [meta, setMeta] = useState(page.metaDescription ?? '');
  const mut = useMutation({
    mutationFn: () => api.patch(`/pages/${pageId}`, { title, metaDescription: meta }),
    onSuccess: onSaved,
  });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <Field label="Page title">
        <Input value={title} onChange={setTitle} />
      </Field>
      <Field label="Meta description" hint="Shows in Google results, ~160 char max">
        <Textarea value={meta} onChange={setMeta} rows={3} />
      </Field>
      <Button onClick={() => mut.mutate()} disabled={mut.isPending}>
        {mut.isPending ? 'Saving…' : 'Save SEO'}
      </Button>
    </div>
  );
}

function AnalyticsTab({ pageId }: { pageId: string }) {
  const q = useQuery({
    queryKey: ['page-analytics', pageId],
    queryFn: () => api.get<{ views: number; conversions: number }>(`/pages/${pageId}/analytics`).catch(() => ({ views: 0, conversions: 0 })),
  });
  if (q.isLoading) return <Spinner />;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
      <Card>
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>Views</div>
        <div style={{ fontSize: 'var(--text-3xl)', fontWeight: 700 }}>{q.data!.views}</div>
      </Card>
      <Card>
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>Conversions</div>
        <div style={{ fontSize: 'var(--text-3xl)', fontWeight: 700 }}>{q.data!.conversions}</div>
      </Card>
    </div>
  );
}

function AbTestTab({ pageId }: { pageId: string }) {
  const [variantSlug, setVariant] = useState('');
  const mut = useMutation({
    mutationFn: () => api.post(`/pages/${pageId}/ab-test`, { variantPageSlug: variantSlug, splitPercent: 50 }),
  });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <Field label="Variant page slug">
        <Input value={variantSlug} onChange={setVariant} placeholder="page-b" />
      </Field>
      <Button onClick={() => mut.mutate()} disabled={!variantSlug || mut.isPending}>
        Start 50/50 test
      </Button>
    </div>
  );
}
